import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  AlertTriangle, ArrowRight, ClipboardList, Home as HomeIcon, FileSearch,
  Users, ShieldCheck, FileBarChart, CalendarRange, Activity,
} from "lucide-react";
import { Reveal, MaskedLine } from "../components/Reveal";
import Seo from "../components/Seo";

const APPROACH = [
  { icon: ClipboardList, color: "#10B981", title: "Structured Medication Reviews", text: "A rolling programme of pharmacist-led SMRs for patients with complex polypharmacy and frailty, prioritised by clinical risk." },
  { icon: HomeIcon, color: "#06B6D4", title: "Care home medication reviews", text: "Regular review cycles for care home residents, aligned to the Enhanced Health in Care Homes framework." },
  { icon: FileSearch, color: "#F59E0B", title: "Prescribing safety audits", text: "Quarterly audits covering high-risk prescribing, opioid and gabapentinoid use, and formulary adherence." },
];

const MODEL = [
  { icon: Users, color: "#10B981", title: "A blended team", text: "One clinical pharmacist supported by a pharmacy technician — the technician carries reconciliation, administration and follow-up so pharmacist time stays clinical." },
  { icon: CalendarRange, color: "#06B6D4", title: "A hybrid week", text: "Remote sessions for reviews and audits; on-site days for care home rounds and patient-facing clinics across the practices." },
  { icon: ShieldCheck, color: "#F59E0B", title: "Named clinical oversight", text: "A named clinical lead, agreed competencies, supervision arrangements and clear escalation routes from day one." },
  { icon: FileBarChart, color: "#EF4444", title: "Monthly reporting", text: "Activity and outcome reporting every month — review volumes, prescribing changes and safety actions, ready for the board." },
];

const NUMBERS = [
  { value: "1,200", label: "Structured Medication Reviews per year", color: "#10B981" },
  { value: "48", label: "Care home review cycles per year", color: "#06B6D4" },
  { value: "600+", label: "GP appointments' worth of capacity released", color: "#F59E0B" },
  { value: "4", label: "Prescribing safety audits delivered", color: "#EF4444" },
];

const CaseStudy = () => (
  <main data-testid="case-study-page">
      <Seo
        title="Case Study: PCN Medicines Optimisation | NCS"
        description="An illustrative worked example of how a managed clinical pharmacy programme could release GP capacity across a Primary Care Network."
      />
    {/* HERO */}
    <section className="relative overflow-hidden pt-32 sm:pt-40 pb-14">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#F59E0B]">Case study — illustrative example</p>
        <h1
          data-testid="case-study-heading"
          className="mt-4 font-display font-extrabold tracking-tight leading-[1.05] text-[#0B192C] text-4xl sm:text-5xl lg:text-6xl"
        >
          <MaskedLine delay={0.15}>How a Medicines Optimisation</MaskedLine>
          <MaskedLine delay={0.3}>Programme Could Release GP</MaskedLine>
          <MaskedLine delay={0.45}>Capacity Across <span className="font-serif-accent">a PCN</span></MaskedLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
          className="mt-6 text-base sm:text-lg leading-relaxed text-[#475569]"
        >
          What happens when a Primary Care Network stops trying to absorb medication workload into
          GP appointments — and commissions a structured clinical pharmacy service instead? This
          worked example shows the shape of a typical engagement.
        </motion.p>
        <Reveal delay={0.2}>
          <div
            data-testid="case-study-disclaimer"
            className="mt-8 flex items-start gap-3 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-2xl px-6 py-4"
          >
            <AlertTriangle size={18} className="text-[#F59E0B] shrink-0 mt-0.5" />
            <p className="text-sm leading-relaxed text-[#0B192C]">
              <strong>This is an illustrative example, not a claim about a real client.</strong>{" "}
              The organisation, figures and outcomes below are a realistic model of how an NCS
              engagement is typically structured — your own proposal would be scoped and quoted
              individually.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10 rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
            <img
              src="/assets/cs-hero.png"
              alt="A clinical pharmacist reviewing a medicines list with an elderly patient during a structured medication review"
              data-testid="case-study-hero-image"
              className="w-full h-[260px] sm:h-[380px] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>

    {/* CHALLENGE */}
    <section className="py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-7">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C]">
            The Challenge: Rising Medication Workload in a Growing Primary Care Network
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569]">
            <p>
              Picture a typical PCN serving around 80,000 patients across six practices. Structured
              Medication Reviews are a Network Contract DES requirement, care home residents need
              regular medication review cycles, and prescribing safety work never stops — but every
              hour spent on it is an hour not spent seeing patients.
            </p>
            <p>
              The network's GPs are carrying medication workload that a clinical pharmacist could
              own entirely. Recruitment has stalled twice. The backlog grows, and with it the
              clinical risk sitting in unreviewed polypharmacy.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.12} className="lg:col-span-5">
          <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
            <img
              src="/assets/cs-carehome.png"
              alt="A pharmacist and care home nurse reviewing medicines together at a medication trolley in a care home"
              data-testid="case-study-carehome-image"
              loading="lazy"
              className="w-full h-[280px] sm:h-[340px] object-cover"
            />
          </div>
        </Reveal>
      </div>
    </section>

    {/* APPROACH */}
    <section className="py-14 sm:py-20 bg-white border-y border-[#E2E8F0]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
            The Approach: A Managed Clinical Pharmacy Service From NCS
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[#475569] max-w-3xl">
            Rather than recruiting, the network commissions a defined programme of work — scoped,
            governed and reported by NCS:
          </p>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {APPROACH.map((a, i) => (
            <Reveal key={a.title} delay={i * 0.08}>
              <div className="h-full bg-[#FAFAFC] border border-[#E2E8F0] rounded-3xl p-8">
                <span
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${a.color}1A`, color: a.color }}
                >
                  <a.icon size={20} />
                </span>
                <h3 className="mt-5 font-display text-lg font-bold text-[#0B192C]">{a.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{a.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* DELIVERY MODEL */}
    <section className="py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C] max-w-3xl">
            The Delivery Model: Structured, Governed and Reported
          </h2>
        </Reveal>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {MODEL.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.07}>
              <div className="h-full bg-white border border-[#E2E8F0] rounded-3xl p-7">
                <span
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: `${m.color}1A`, color: m.color }}
                >
                  <m.icon size={20} />
                </span>
                <h3 className="mt-5 font-display text-base font-bold text-[#0B192C] leading-snug">{m.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-[#475569]">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* NUMBERS */}
    <section className="py-14 sm:py-20 bg-[#0B192C]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center">
          <p className="text-xs uppercase tracking-[0.22em] font-mono-brand text-[#C5A059]">Illustrative figures</p>
          <h2 className="mt-3 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white max-w-3xl mx-auto">
            The Illustrative Impact: What a Year of This Programme Could Look Like
          </h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {NUMBERS.map((n, i) => (
            <Reveal key={n.label} delay={i * 0.08}>
              <div data-testid={`case-study-stat-${i + 1}`} className="bg-white/5 border border-white/10 rounded-3xl p-7 text-center backdrop-blur-sm">
                <p className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight" style={{ color: n.color }}>
                  {n.value}
                </p>
                <p className="mt-3 text-sm text-white/60 leading-snug">{n.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal className="text-center mt-8">
          <p className="text-xs text-white/40 max-w-xl mx-auto leading-relaxed">
            Illustrative modelling only — actual volumes, capacity impact and costs depend on your
            patient cohort, estate and scope, and are confirmed in a formal proposal.
          </p>
        </Reveal>
      </div>
    </section>

    {/* MEANING + CTA */}
    <section className="py-14 sm:py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <div className="rounded-3xl overflow-hidden border border-[#E2E8F0] shadow-[0_10px_30px_rgba(11,25,44,0.04)]">
            <img
              src="/assets/cs-team.png"
              alt="A clinical pharmacist and pharmacy technician reviewing prescribing reports together at a GP practice desk"
              data-testid="case-study-team-image"
              loading="lazy"
              className="w-full h-[280px] sm:h-[340px] object-cover"
            />
          </div>
        </Reveal>
        <Reveal className="lg:col-span-7 order-1 lg:order-2">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B192C]">
            What This Could Mean for Your Organisation
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-[#475569]">
            <p>
              Medication workload moves out of GP appointments and into a governed clinical pharmacy
              service. DES-aligned work gets done on schedule. Prescribing safety stops being a
              backlog and becomes a programme. And your board gets reporting that proves it.
            </p>
            <p>
              Whether you are a PCN clinical director, a practice manager or an ICB commissioner,
              the starting point is the same: a two-minute enquiry.
            </p>
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/commission-clinical-services"
              data-testid="case-study-cta-commission"
              className="group inline-flex items-center gap-2.5 bg-[#0B192C] text-white font-bold px-7 py-4 rounded-full text-sm transition-colors duration-300 hover:bg-[#0F382C]"
            >
              Commission Clinical Services
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              data-testid="case-study-cta-contact"
              className="inline-flex items-center gap-2.5 bg-white text-[#0B192C] font-bold px-7 py-4 rounded-full text-sm border border-[#E2E8F0] transition-colors duration-300 hover:border-[#0B192C]"
            >
              <Activity size={16} className="text-[#06B6D4]" />
              Discuss Your Situation
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </main>
);

export default CaseStudy;
